import { Button } from "@/components/ui/button";
import {
  useCallbackStatus,
  useRequestCallback,
} from "@/features/onboarding/hooks/use-onboarding";
import { onboardingErrorMessage } from "@/features/onboarding/errors";
import type { OnboardingResult } from "@/features/onboarding/types";
import { useAgents } from "@/features/agents/hooks/use-agents";
import { useTranslation } from "@/features/utils/i18n";
import { AgentTestButton } from "@/views/backoffice/agents/components/agent-test";
import {
  CheckIcon,
  PhoneCallIcon,
  PhoneIncomingIcon,
  WarningCircleIcon,
} from "@phosphor-icons/react";
import { QRCodeSVG } from "qrcode.react";
import { useEffect, useRef, useState } from "react";
import { formatPhoneNumberIntl } from "react-phone-number-input";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { welcomeNavigationState } from "../welcome-dialog";
import {
  AgentBubble,
  BlockBubble,
  OnboardingChatShell,
  useOnboardingChat,
} from "./chat";

/**
 * The payoff: the agent the client just built, on the phone.
 *
 * Three beats, in the order the client lives them:
 * 1. the demo number — shown whatever the client picked, because calling it
 *    back from their mobile is how they reach the agent until a number of
 *    their own is ready — and "Appelez-moi", which has the agent ring them;
 * 2. "l'appel est en cours", following the call, with "C'est tout bon";
 * 3. which opens the dashboard, where "Bravo, bienvenue chez HeyUp" and what
 *    is left to do go off with the confetti (`WelcomeDialog`).
 *
 * The number is grouped the way its own country writes it, because it is one
 * to key in by hand, and a QR code opens the dialler on it for a client looking
 * at this screen with the very phone they must dial from.
 *
 * When there is no demo line — the platform doesn't run one, or the client's
 * mobile already demos for another tenant — we say so plainly and let them
 * carry on, rather than pretending the setup half-worked.
 */
export function StepDone({
  result,
  clientId,
  initialCallback = null,
}: {
  result: OnboardingResult;
  clientId: string;
  /** The call placed from the last step ("Recevoir un appel"), if one was. */
  initialCallback?: { sessionId: string; remaining: number } | null;
}) {
  const { t } = useTranslation("onboarding");
  const navigate = useNavigate();
  const { agentName } = useOnboardingChat();

  const canCall = Boolean(result.demo_number && result.caller_number);

  const demoNumber = result.demo_number || "";
  // `formatPhoneNumberIntl` returns "" for anything it can't parse, in which
  // case the raw string is the best we have. The `tel:` URI keeps the E.164
  // either way — spaces in a dialled number are a per-dialler gamble.
  const demoNumberDisplay = formatPhoneNumberIntl(demoNumber) || demoNumber;
  const callerNumberDisplay = result.caller_number
    ? formatPhoneNumberIntl(result.caller_number) || result.caller_number
    : "";

  const requestCallback = useRequestCallback();
  const [callback, setCallback] = useState<{
    sessionId: string;
    remaining: number;
  } | null>(initialCallback);
  const { data: callStatus = "calling" } = useCallbackStatus(
    callback?.sessionId ?? null,
  );

  // The thread only follows new steps on its own; the call is news inside this
  // one, so bring it into view when it starts.
  const callRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (callback) {
      callRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [callback]);

  const callMe = async () => {
    try {
      const placed = await requestCallback.mutateAsync();
      setCallback({
        sessionId: placed.session_id,
        remaining: placed.remaining,
      });
    } catch (error) {
      toast.error(onboardingErrorMessage(error, t, "errors.callbackFailed"));
    }
  };

  const finish = () =>
    navigate(`/${clientId}/home`, {
      state: welcomeNavigationState({ agentName }),
    });

  // The agent the wizard just configured: onboarding personalises the tenant's
  // first agent rather than creating one (`applyAgent`), so it is the first of
  // the list. A client without a phone at hand can still hear it, from here.
  const { data: agents } = useAgents();
  const agent = agents?.[0];
  const testInBrowser = (label: string) =>
    agent ? (
      <AgentTestButton
        agent={agent}
        variant="link"
        className="h-auto p-0"
        label={label}
      />
    ) : null;

  const allGood = (
    <Button type="button" className="w-full" onClick={finish}>
      <CheckIcon className="h-4 w-4" weight="bold" />
      {t("steps.done.allGood")}
    </Button>
  );

  if (!canCall) {
    return (
      <OnboardingChatShell step="done">
        <AgentBubble>{t("steps.done.intro.none")}</AgentBubble>
        <BlockBubble>
          <div className="flex gap-3">
            <WarningCircleIcon className="text-muted-foreground mt-0.5 h-5 w-5 shrink-0" />
            <div className="space-y-1 text-sm">
              <p className="font-medium">{t("steps.done.noDemoTitle")}</p>
              <p className="text-muted-foreground">
                {t("steps.done.noDemoDescription")}
              </p>
              <div>{testInBrowser(t("steps.done.testHere"))}</div>
            </div>
          </div>
          <div className="mt-4">{allGood}</div>
        </BlockBubble>
      </OnboardingChatShell>
    );
  }

  return (
    <OnboardingChatShell step="done">
      <AgentBubble>{t("steps.done.intro.call")}</AgentBubble>

      <BlockBubble>
        <div className="border-primary/30 bg-primary/5 rounded-xl border-2 p-5 text-center">
          <PhoneCallIcon
            className="text-primary mx-auto h-7 w-7"
            weight="fill"
          />
          <p className="text-muted-foreground mt-2 text-sm">
            {t("steps.done.callPrompt")}
          </p>
          <a
            href={`tel:${demoNumber}`}
            className="mt-1 block text-3xl font-bold tracking-tight hover:underline"
          >
            {demoNumberDisplay}
          </a>
          <p className="text-muted-foreground mt-2 text-xs">
            {t("steps.done.callFrom", { number: callerNumberDisplay })}
          </p>

          <div className="mt-5 flex flex-col items-center gap-2">
            {/* White plate regardless of theme: a QR code inverted by a dark
                background is a QR code most scanners refuse. */}
            <div className="rounded-lg bg-white p-3">
              <QRCodeSVG
                value={`tel:${demoNumber}`}
                size={128}
                bgColor="#ffffff"
                fgColor="#000000"
                level="M"
              />
            </div>
            <p className="text-muted-foreground text-xs">
              {t("steps.done.scanToCall")}
            </p>
          </div>
        </div>

        {!callback && (
          <div className="mt-4 space-y-3 text-center">
            <Button
              type="button"
              className="w-full"
              onClick={() => void callMe()}
              disabled={requestCallback.isPending}
            >
              <PhoneIncomingIcon className="h-4 w-4" weight="fill" />
              {t("steps.done.callMe", {
                name: agentName?.trim() || t("chat.agentFallback"),
              })}
            </Button>
            <div className="flex flex-col items-center gap-1">
              {testInBrowser(t("steps.done.testInBrowser"))}
              <Button
                type="button"
                variant="link"
                className="text-muted-foreground h-auto p-0"
                onClick={finish}
              >
                {t("steps.done.alreadyCalled")}
              </Button>
            </div>
          </div>
        )}
      </BlockBubble>

      {callback && (
        <>
          <AgentBubble>
            {t(`steps.done.call.${callStatus}`, {
              number: callerNumberDisplay,
            })}
          </AgentBubble>
          <BlockBubble>
            <div className="flex items-center gap-3">
              <span className="relative flex h-10 w-10 shrink-0 items-center justify-center">
                {callStatus === "calling" && (
                  <span className="bg-primary/30 absolute inset-0 animate-ping rounded-full motion-reduce:animate-none" />
                )}
                <span className="bg-primary/10 relative flex h-10 w-10 items-center justify-center rounded-full">
                  <PhoneCallIcon
                    className="text-primary h-5 w-5"
                    weight="fill"
                  />
                </span>
              </span>
              <div className="min-w-0 flex-1 text-sm">
                <p className="font-medium">
                  {t(`steps.done.callStatus.${callStatus}`)}
                </p>
                <p className="text-muted-foreground">{callerNumberDisplay}</p>
              </div>
              {callStatus === "missed" && callback.remaining > 0 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => void callMe()}
                  disabled={requestCallback.isPending}
                >
                  {t("steps.done.callAgain")}
                </Button>
              )}
            </div>
            <div className="mt-4">{allGood}</div>
          </BlockBubble>
          <div ref={callRef} />
        </>
      )}
    </OnboardingChatShell>
  );
}
